import React, { useState } from 'react';

/**
 * <LicenseJwtModal> - zeigt das Ergebnis einer Lizenzausstellung.
 *
 * Signiert (result.signed === true):  result.license = JWS Compact -> Copy + Download.
 * Unsigniert:                         nur result.claims -> klarer Hinweis, dass kein
 *                                     Signierschluessel konfiguriert ist (ADR-0018 §10),
 *                                     Claims zum Offline-Signieren kopier-/ladbar.
 *
 * Props: result, tenantLabel, toolId, onClose, t.
 */
export default function LicenseJwtModal({ result, tenantLabel, toolId, onClose, t }) {
  const [copied, setCopied] = useState('');

  if (!result) return null;
  const signed = !!result.signed;
  const jwt = result.license || '';
  const claimsText = JSON.stringify(result.claims || {}, null, 2);
  const payloadText = signed ? jwt : claimsText;
  const filename = result.filename
    || `license-${tenantLabel || 'tenant'}-${toolId || 'tool'}.${signed ? 'jwt' : 'claims.json'}`;

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(''), 1800);
    } catch {
      setCopied('');
    }
  };

  const download = (text, name, mime) => {
    try {
      const blob = new Blob([text], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      /* no-op */
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9100,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 12, width: '92%', maxWidth: 680, padding: 26,
          maxHeight: '88vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
          {t('license.issue.result_title', 'Lizenz ausgestellt')}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
          {tenantLabel} - {toolId} - JTI {result.jti || '-'}
        </div>

        {signed && (
          <div
            style={{
              background: 'rgba(37,64,95,.10)', border: '1px solid rgba(37,64,95,.35)',
              borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: 'var(--text)',
              marginBottom: 16, lineHeight: 1.6,
            }}
          >
            <strong>{t('license.issue.redeploy_title', 'Neue Lizenzdatei - beim Kunden neu einspielen')}</strong><br />
            {t('license.issue.redeploy_hint',
               'Diese Datei muss auf der Kundeninstanz die bisherige Lizenzdatei ersetzen und der Dienst neu gestartet/neu geladen werden - erst danach gelten die geaenderten Konditionen (Gueltigkeit/Bindung/Durchsetzung/Entitlements). Ein Widerruf greift zusaetzlich ueber den Heartbeat, sofern online.')}
          </div>
        )}

        {!signed && (
          <div
            style={{
              background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.35)',
              borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#b45309',
              marginBottom: 16, lineHeight: 1.6,
            }}
          >
            {t('license.issue.unsigned_hint',
               'Kein Signierschluessel konfiguriert (ADR-0018 Paragraph 10). Es werden nur die Claims zur Offline-Signierung durch einen separaten Signierdienst zurueckgegeben - diese sind noch KEINE einsatzfaehige Lizenzdatei.')}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
            {signed
              ? t('license.issue.jwt_label', 'Signierte Lizenz (JWS)')
              : t('license.issue.claims_label', 'Lizenz-Claims (unsigniert)')}
          </div>
        </div>

        <textarea
          readOnly
          value={payloadText}
          style={{
            width: '100%', minHeight: signed ? 110 : 220, fontFamily: 'monospace',
            fontSize: 11.5, padding: 10, borderRadius: 8,
            border: '1px solid var(--border)', background: 'var(--bg-surface)',
            color: 'var(--text)', resize: 'vertical', boxSizing: 'border-box',
          }}
        />

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => copy(payloadText, 'main')}
          >
            {copied === 'main'
              ? t('license.issue.copied', 'Kopiert')
              : t('license.issue.copy', 'In Zwischenablage kopieren')}
          </button>
          {signed && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => download(payloadText, filename, 'application/jwt')}
            >
              {t('license.issue.download', 'Herunterladen')} ({filename})
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.close', 'Schliessen')}
          </button>
        </div>
      </div>
    </div>
  );
}
